import { get } from 'stash/utils';
import { isEmpty } from 'stash/utils/helpers';
import { PERSONAL_BROKERAGE } from 'stash/constants/accountTypes';

import { TEST_ASSIGNMENT_TYPES, TEST_NAMES } from 'stash/constants/testAssignmentTypes';
import { pxQuestionsAnswerTypes } from 'stash/components/screens/state-zero-home/constants';
import {
	IDV_PX_QUESTION_NAME,
	idvPxQuestionsAnswerTypes,
	idvScreenNames,
} from 'stash/constants/identityVerification';
import { getLocalStorageFlag } from '../utils/storage.js';
import { getBankLinkPlaidTokenCookie } from 'stash/components/screens/payment-source/utils.js';

export const hasFirstLastName = (state) => {
	const { user } = state.entities;
	return !!(user.first_name && user.last_name);
};

export const hasDateOfBirth = (state) => {
	const date_of_birth = get(state, 'entities.profile.date_of_birth');
	return !!(date_of_birth && date_of_birth !== '1900-01-01');
};

export const hasTrustedContact = (state) => {
	const trusted_contact = get(state, 'entities.user.trusted_contact', {});
	return !!(trusted_contact && trusted_contact.user_id);
};

export const hasOptedOutTrustedContact = (state) => {
	const trusted_contact = get(state, 'entities.user.trusted_contact', {});
	return !!(trusted_contact && trusted_contact.opted_out_at);
};

export const completedAddTrustedContact = (state) => {
	return get(state, 'application.signUp.completed.add-trusted-contact');
};

export const completedTrustedContact = (state) => {
	return hasTrustedContact(state) || hasOptedOutTrustedContact(state);
};

export const getEmploymentStatus = (state) => {
	return get(state, 'entities.onboarding_answers.employment_status.answers');
};

export const hasNonFulltimeEmployment = (state) => {
	const employment_status = getEmploymentStatus(state);
	return employment_status.length && !employment_status.includes('EMPLOYED');
};

export const hasEmployer = (state) => {
	const { employer, employment_status } = state.entities.investor_application;
	return !!employer && !!employment_status;
};

export const hasCompletedInvestingExperience = (state) => {
	const {
		household_income_bracket,
		total_net_worth_range,
		risk_tolerance,
		time_horizon,
	} = state.entities.onboarding_answers;
	return (
		household_income_bracket.answers.length &&
		total_net_worth_range.answers.length &&
		risk_tolerance.answers.length &&
		time_horizon.answers.length
	);
};

export const hasAffiliation = (state) => {
	return !!get(state, 'entities.investor_application.is_affiliated_exchange_or_finra');
};

export const hasUSCitizenship = (state) => {
	const country = get(state, 'entities.profile.citizenship_country');
	return country === 'USA';
};

export const hasUSCitizenshipAndDOB = (state) => {
	return hasUSCitizenship(state) && hasDateOfBirth(state);
};

export const hasFirstLastNameAndUSCitizenshipAndDOB = (state) => {
	return hasFirstLastName(state) && hasUSCitizenshipAndDOB(state);
};

export const hasGreenCard = (state) => {
	const greenCard = get(state, 'entities.profile.permanent_resident');
	return greenCard === 'YES';
};

export const hasGreenCardAndDOB = (state) => {
	return hasGreenCard(state) && hasDateOfBirth(state);
};

export const hasNoGreenCard = (state) => {
	const greenCard = get(state, 'entities.profile.permanent_resident');
	return greenCard === 'NO';
};

export const hasNoGreenCardAndDOB = (state) => {
	return hasNoGreenCard(state) && hasDateOfBirth(state);
};

export const hasEligibleVisa = (state) => {
	const visa_type = get(state, 'entities.profile.visa_type');
	return !!visa_type;
};

export const hasCitizenshipCountry = (state) => {
	return (
		!!get(state, 'entities.profile.citizenship_country') && !isPlasticScreenSkipped(state)
	);
};

export const hasAddress = (state) => {
	const profile = get(state, 'entities.profile', {});

	return !!(
		profile.home_city &&
		profile.home_postal_code &&
		profile.home_state &&
		profile.home_street_address &&
		profile.home_street_address.length
	);
};

export const hasPhoneNumber = (state) => {
	return !!get(state, 'entities.profile.phone_number');
};

export const hasVerifiedPhoneNumber = (state) => {
	return (
		hasPhoneNumber(state) &&
		get(state, 'entities.user.phone_number_verification.verified')
	);
};

export const hasSSN = (state) => {
	return !!get(state, 'entities.profile.encrypted_social_security_number');
};

export const hasSSNComplete = (state) => {
	return hasSSN(state) || hasValidSSNComplete(state);
};

export const _getInvestAccount = (state) => {
	const investAccountId = get(state, 'entities.account_by_type.PERSONAL_BROKERAGE');
	return investAccountId && get(state, `entities.account.${investAccountId}`);
};

export const _getRetireAccount = (state) => {
	const rothAccountId = get(state, 'entities.account_by_type.ROTH_IRA');
	const tradAccountId = get(state, 'entities.account_by_type.TRADITIONAL_IRA');
	const retireAccountId = rothAccountId || tradAccountId;
	return retireAccountId && get(state, `entities.account.${retireAccountId}`);
};

export const hasAdvisorySignature = (state) => {
	return !!state.entities.advisory_agreements?.signature?.esignedAt;
};

export const hasAdvisorySignatureAndNoSmartEntitlement = (state) => {
	return (
		hasAdvisorySignature(state) &&
		state?.application?.signUp?.entitlementFlags?.hasPersonalEnititlement &&
		!state?.application?.signUp?.entitlementFlags?.hasSmartEntitlement
	);
};

export const hasAccountSignatureAndNoSmartEntitlement = (state) => {
	return (
		hasInvestEsig(state) &&
		state?.application?.signUp?.entitlementFlags?.hasPersonalEnititlement &&
		!state?.application?.signUp?.entitlementFlags?.hasSmartEntitlement
	);
};

export const hasAdvisorySignatureAndAddress = (state) => {
	return hasAdvisorySignature(state) && hasAddress(state);
};

export const hasInvestEsig = (state) => {
	const acctId = get(state, `entities.account_by_type.${PERSONAL_BROKERAGE}`);
	return !!(
		get(state, `entities.account.${acctId}.eSignatureAgreedAt`) ||
		get(state, `entities.account.${acctId}.e_signature_agreed_at`)
	);
};

export const hasNextStepAsRoboPersonalBrokerage = (state) => {
	return (
		get(state, `application.eSig.eSigNextStep`) ===
		pxQuestionsAnswerTypes.ROBO_PERSONAL_BROKERAGE_ACCOUNT
	);
};

export const hasNextStepAsPersonalBrokerage = (state) => {
	return (
		get(state, `application.eSig.eSigNextStep`) ===
		pxQuestionsAnswerTypes.PERSONAL_BROKERAGE_ACCOUNT
	);
};

export const hasInvestEsigWithNoNextStep = (state) => {
	const hasEsigned = hasInvestEsig(state);
	const hasNoNextStep = get(state, `application.eSig.eSigNextStep`) === undefined;
	return hasEsigned && hasNoNextStep;
};

export const hasStateZeroWithPersonal = (state) => {
	return hasInvestEsig(state) && hasNextStepAsPersonalBrokerage(state);
};

export const hasStateZeroWithSmart = (state) => {
	return hasInvestEsig(state) && hasNextStepAsRoboPersonalBrokerage(state);
};

export const hasFirstPurchase = (state) => {
	const firstPurchase = get(state, 'application.signUp.firstPurchase', {});
	return !!(firstPurchase.amount && firstPurchase.card_id);
};

export const hasAccountAutostash = (state) => {
	const investAccount = _getInvestAccount(state);
	const retireAccount = _getRetireAccount(state);

	const hasInvestAutoStash =
		investAccount &&
		investAccount.auto_stash &&
		investAccount.auto_stash.total_allocated_amount &&
		investAccount.auto_stash.frequency;

	const hasRetireAutoStash =
		retireAccount &&
		retireAccount.auto_stash &&
		retireAccount.auto_stash.total_allocated_amount &&
		retireAccount.auto_stash.frequency;

	return !!(hasInvestAutoStash || hasRetireAutoStash);
};

export const hasAccountPurchase = (state) => {
	const investAccount = _getInvestAccount(state);
	const retireAccount = _getRetireAccount(state);

	const hasInvestPurchase = investAccount && !isEmpty(investAccount.cards);
	const hasRetirePurchase = retireAccount && !isEmpty(retireAccount.cards);

	return !!(hasInvestPurchase || hasRetirePurchase);
};

export const hasBankLinked = (state) => {
	const manage_external_account = get(state, 'entities.manage_external_account');
	return !!(manage_external_account && manage_external_account.bankAccount?.state);
};

export const hasDatabaseTransaction = (state) => {
	return hasAccountAutostash(state) || hasAccountPurchase(state);
};

export const hasBankLinkedWithSkipToLinkBankPrimer = (state) => {
	return hasBankLinked(state) && skipToLinkBankPrimer(state);
};

export const hasBankLinkedWithSetScheduleValuesAndRoboAsNextStep = (state) => {
	console.log(
		'plaid transition hasBankLinkedWithSetScheduleCompleteAndSmartV3',
		hasBankLinked(state),
		hasSetScheduleAmountAndFrequency(state),
		hasNextStepAsRoboPersonalBrokerage(state)
	);
	return (
		hasBankLinked(state) &&
		hasSetScheduleAmountAndFrequency(state) &&
		hasNextStepAsRoboPersonalBrokerage(state)
	);
};

export const hasBankLinkedWithDatabaseTransaction = (state) => {
	return hasBankLinked(state) && hasDatabaseTransaction(state);
};

export const isPlasticScreenSkipped = (state) => {
	const testType = get(
		state,
		`application.testAssignment.${TEST_NAMES.SKIP_PLASTIC}`,
		TEST_ASSIGNMENT_TYPES.CONTROL
	);
	const isSetUpLaterClicked = get(state, 'application.signUp.completed.skip-plastic');
	return (
		testType === TEST_ASSIGNMENT_TYPES.SKIP_PLASTIC ||
		(testType === TEST_ASSIGNMENT_TYPES.SKIP_PLASTIC_WITH_BUTTON && isSetUpLaterClicked)
	);
};

export const hasInvestProduct = (state) => {
	const productTypes = get(state, 'entities.user.platform_tier.product_types');
	return !!productTypes && productTypes.includes('PERSONAL_BROKERAGE');
};

export const hasCurrentPlan = (state) => {
	return state?.entities?.dynamic_platform_tiers?.currentPlan?.id;
};

export const hasPlanSelectionCompleteAndCurrentPlan = (state) => {
	return (
		state?.application?.signUp?.completed?.['set-plan-selection-complete'] &&
		hasCurrentPlan(state)
	);
};

export const completedBillingSummary = (state) => {
	return get(state, 'application.signUp.completed.billing-summary');
};

export const completedWelcomeToStash = (state) => {
	const isScreenCompleted = getLocalStorageFlag('welcomeScreenCompleted');
	return (
		get(state, 'application.signUp.completed.set-welcome-to-stash-complete') ||
		isScreenCompleted
	);
};

export const completedWelcomeToStashWithNoSmartEntitlement = (state) => {
	const isScreenCompleted = getLocalStorageFlag('welcomeScreenCompleted');
	return (
		(state?.application?.signUp?.completed?.['set-welcome-to-stash-complete'] ||
			isScreenCompleted) &&
		state?.application?.signUp?.entitlementFlags?.hasPersonalEnititlement &&
		!state?.application?.signUp?.entitlementFlags?.hasSmartEntitlement
	);
};

export const hasAutoInvestMix = (state) => {
	return get(state, 'application.signUp.completed.set-auto-invest-mix');
};

export const hasAddCash = (state) => {
	return get(state, 'application.signUp.completed.set-add-cash');
};

export const hasAddCashAmountComplete = (state) => {
	return get(state, 'application.signUp.completed.set-add-cash-amount-complete');
};

export const hasAddCashSummaryComplete = (state) => {
	return get(state, 'application.signUp.completed.set-add-cash-summary-complete');
};

export const hasAddCashSuccessComplete = (state) => {
	return get(state, 'application.signUp.completed.set-add-cash-success-complete');
};

export const skipToEnd = (state) => {
	return get(state, 'application.signUp.completed.skip-to-end');
};

export const setScheduleComplete = (state) => {
	return get(state, 'application.signUp.completed.set-schedule-complete');
};

export const hasSetScheduleAmountAndFrequency = (state) => {
	console.log(
		'plaid hasSetScheduleAmountAndFrequency',
		!!get(state, 'application.setSchedule.amount'),
		get(state, 'application.setSchedule.amount') > 0,
		get(state, 'application.setSchedule.amount'),
		!!get(state, 'application.setSchedule.frequency')
	);
	return (
		!!get(state, 'application.setSchedule.amount') &&
		get(state, 'application.setSchedule.amount') > 0 &&
		!!get(state, 'application.setSchedule.frequency')
	);
};

export const setScheduleAmountZero = (state) => {
	return get(state, 'application.setSchedule.amount') === 0;
};

export const hasAutoInvestSmartSummaryComplete = (state) => {
	return get(
		state,
		'application.signUp.completed.set-auto-invest-smart-summary-complete'
	);
};

export const hasSmartSuccessComplete = (state) => {
	return get(state, 'application.signUp.completed.set-smart-success-complete');
};

export const hasPersonalPortfolioEscapeHatchSelected = (state) => {
	return get(
		state,
		'application.signUp.completed.set-personal-portfolio-escape-hatch-selected'
	);
};

export const skipToLinkBankPrimer = (state) => {
	return get(state, 'application.signUp.completed.skip-to-link-bank-primer');
};

export const hasLinkBankPrimerComplete = (state) => {
	return get(state, 'application.signUp.completed.set-link-bank-primer-complete');
};

export const hasOnboardingComplete = (state) => {
	return get(state, 'application.signUp.completed.set-onboarding-complete');
};

// Smart Reward Transitions

export const setSmartPortfolioAllocationComplete = (state) => {
	return get(state, 'application.signUp.completed.smart-portfolio-allocation-complete');
};

export const hasSmartPortfolioTransitionComplete = (state) => {
	return get(state, 'application.signUp.completed.smart-portfolio-transition-complete');
};

export const setDuplicateSSNBlockScreen = (state) => {
	return get(state, 'application.signUp.completed.set-duplicate-ssn-block-screen');
};

export const hasValidSSNComplete = (state) => {
	return get(state, 'application.signUp.completed.valid-ssn-complete');
};

export const hasStateZeroHomeComplete = (state) => {
	return get(state, 'application.signUp.completed.set-state-zero-home-complete');
};

export const hasStateZeroPrimerComplete = (state) => {
	return get(state, 'application.signUp.completed.set-state-zero-primer-complete');
};

export const hasStateZeroPrimerCompleteWithSsn = (state) => {
	return (
		get(state, 'application.signUp.completed.set-state-zero-primer-complete') &&
		hasSSN(state)
	);
};

export const hasStateZeroHomeCompleteWithIdvStatusAndHardFail = (state) => {
	return (
		hasStateZeroHomeComplete(state) &&
		(state?.application?.idvStatus?.nextScreen === idvScreenNames.HARD_FAIL ||
			state?.application?.idvStatus?.nextScreen === idvScreenNames.ALLOY_OUTAGE)
	);
};

export const hasIdvAsCelebrationAndPxQuestionIdvStatusComplete = (state) => {
	return (
		get(state, 'application.idvStatus.nextScreen') === idvScreenNames.CELEBRATION &&
		state?.entities?.['px_questions']?.[IDV_PX_QUESTION_NAME]?.answer?.value ===
			idvPxQuestionsAnswerTypes.COMPLETE
	);
};

export const hasIdvVerificationLoaderCompleteWithNextScreenAsCelebration = (state) => {
	return (
		get(state, 'application.signUp.completed.set-idv-check-loader-complete') &&
		get(state, 'application.idvStatus.nextScreen') === idvScreenNames.CELEBRATION
	);
};

export const hasIdvVerificationLoaderCompleteWithNextScreenAsError = (state) => {
	return (
		get(state, 'application.signUp.completed.set-idv-check-loader-complete') &&
		(get(state, 'application.idvStatus.nextScreen') === idvScreenNames.ALLOY_OUTAGE ||
			get(state, 'application.idvStatus.nextScreen') === idvScreenNames.HARD_FAIL)
	);
};

export const hasIdvVerificationLoaderCompleteWithNextScreenAsManualReview = (state) => {
	return (
		get(state, 'application.signUp.completed.set-idv-check-loader-complete') &&
		get(state, 'application.idvStatus.nextScreen') === idvScreenNames.MANUAL_REVIEW
	);
};

export const hasIDVSuccessCompleteWithAdvisoryEsig = (state) => {
	return (
		state?.application?.signUp?.completed?.['set-idv-success-complete'] &&
		hasAdvisorySignature(state)
	);
};

const hasIDVSuccessComplete = (state) => {
	return state?.application?.signUp?.completed?.['set-idv-success-complete'];
};

export const hasIDVSuccessCompleteAndNoAdvisoryEsig = (state) => {
	return hasIDVSuccessComplete(state) && !hasAdvisorySignature(state);
};

export const hasReturnToIdvFlow = (state) => {
	return state?.application?.idvStatus?.isReturnedFromIDVFlow;
};

export const hasBankLinkOAuthSession = (state) => {
	return (
		getBankLinkPlaidTokenCookie() &&
		get(state, 'application.signUp.completed.bank-link-oauth')
	);
};

export const hasBankLinkOAuthRedirectUri = (state) => {
	console.log(
		'plaid transition',
		getBankLinkPlaidTokenCookie(),
		!!get(state, 'application.linkBank.oauth.redirectUri')
	);
	return (
		getBankLinkPlaidTokenCookie() &&
		!!get(state, 'application.linkBank.oauth.redirectUri')
	);
};
